import { useEffect, useRef, useState } from "react";
import { ImageIcon, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, mediaUrl } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function ImagePicker({ label = "Image", value, onChange }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) api.get("/admin/media").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  }, [open]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", file.name);
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image téléversée");
      onChange(data.url);
      setOpen(false);
    } catch (err) {
      toast.error(apiError(err, "Téléversement impossible"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1.5 flex items-center gap-3 flex-wrap">
        {value ? (
          <div className="relative border border-line bg-sand p-2">
            <img src={mediaUrl(value)} alt="" className="h-20 w-auto object-contain" />
            <button type="button" onClick={() => onChange("")} data-testid="image-picker-clear" aria-label="Retirer l'image"
              className="absolute -top-2 -right-2 w-6 h-6 bg-onyx text-white flex items-center justify-center hover:bg-rouge">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="h-20 w-20 border border-dashed border-input bg-sand flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-muted-foreground" />
          </div>
        )}
        <button type="button" onClick={() => setOpen(true)} data-testid="image-picker-open"
          className="text-sm font-bold uppercase tracking-wide border-2 border-onyx px-4 py-2 hover:bg-onyx hover:text-cream transition-colors">
          {value ? "Remplacer" : "Choisir une image"}
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="image-picker-dialog">
          <DialogHeader><DialogTitle className="font-display">Médiathèque</DialogTitle></DialogHeader>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="image-picker-upload"
            className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-input py-6 text-sm font-bold uppercase tracking-wide text-onyx hover:border-rouge hover:text-rouge transition-colors disabled:opacity-50">
            <Upload className="w-4 h-4" /> {uploading ? "Téléversement…" : "Téléverser une nouvelle image"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
            {items.map((m) => (
              <button key={m.id} type="button" onClick={() => { onChange(m.url); setOpen(false); }}
                data-testid={`image-picker-item-${m.id}`}
                className="aspect-square bg-sand border border-line hover:border-rouge p-2 flex items-center justify-center transition-colors">
                <img src={mediaUrl(m.url)} alt={m.alt} loading="lazy" className="max-h-full max-w-full object-contain" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
